import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api';
import { jobEndpoints, appointmentAuthEndpoints } from '@/lib/api/endpoints';
import { jobKeys, isJobAppointmentsQuery } from '@/lib/queryKeys';
import type { AuthorizationLevel, SignoffMethod } from '@/types/job';

export interface SignoffConfigDto {
  required: boolean;
  level: AuthorizationLevel;
  method: SignoffMethod;
}

export function useUpdateSignoffConfig(entity: 'job' | 'appointment' = 'job') {
  const qc = useQueryClient();
  return useApiMutation<unknown, SignoffConfigDto & { id: string }>({
    mutationFn: ({ id, ...dto }) => {
      const url =
        entity === 'job' ? jobEndpoints.signoff(id) : appointmentAuthEndpoints.signoff(id);
      return apiPatch(operationsClient, url, dto);
    },
    onSuccess: (_data, vars) => {
      if (entity === 'job') {
        void qc.invalidateQueries({ queryKey: jobKeys.detail(vars.id) });
      } else {
        // The mutation only carries the appointment id, not its jobId, so match
        // any cached job-appointments list/detail query and refetch it.
        void qc.invalidateQueries({ predicate: isJobAppointmentsQuery });
      }
      toast.success('Sign-off settings updated');
    },
  });
}
